import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigationType } from "react-router-dom";
import API_ENDPOINTS from "../utilities/APIRoutes";
import useStreamingChat from "./useStreamingChat";
import { loadSettings } from "../pages/UserSettings";

// Quick chat state model — matches a browser-incognito tab:
// • session_id and the message bubble list are mirrored to sessionStorage so
//   a page reload feels seamless. Closing the tab clears them (browser does
//   that for us). User-initiated "end chat" and server-side expiry (404/410)
//   also clear them explicitly.
// • Active navigation TO /quick-chat from another route (clicking a sidebar
//   link, calling navigate()) wipes the persisted state on mount — going to
//   /main-chat and back should feel like starting fresh, not resuming. Only
//   reload / browser back/forward (POP) preserves.
// • The attached file is intentionally NOT persisted — File objects can't
//   round-trip through sessionStorage, and re-uploading silently after a
//   reload would surprise the user.
// • localStorage / IndexedDB / cookies are still off-limits.

const SESSION_KEY = "ace_quick_chat_session_id";
const MESSAGES_KEY = "ace_quick_chat_messages";

const readStoredSession = () => {
  try {
    return sessionStorage.getItem(SESSION_KEY) || null;
  } catch {
    return null;
  }
};

const writeStoredSession = (id) => {
  try {
    if (id) sessionStorage.setItem(SESSION_KEY, id);
    else sessionStorage.removeItem(SESSION_KEY);
  } catch {
    // Privacy-mode browsers may throw on sessionStorage writes. Behave as if
    // we have no persistence — the in-memory ref still works for this tab.
  }
};

// Strip transient flags before persisting — the streaming/loading state from
// a prior render doesn't make sense after a reload (the request is gone).
// `attachment` is metadata only (name/size/type), not the File object itself,
// so it's safe to round-trip through JSON.
const sanitizeMessageForStorage = (m) => ({
  id: m.id,
  sender: m.sender,
  message: m.message || "",
  ...(m.hasError ? { hasError: true } : {}),
  ...(m.errorKind ? { errorKind: m.errorKind } : {}),
  ...(m.attachment
    ? {
        attachment: {
          name: m.attachment.name,
          size: m.attachment.size,
          type: m.attachment.type,
        },
      }
    : {}),
});

const readStoredMessages = () => {
  try {
    const raw = sessionStorage.getItem(MESSAGES_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    // Defensive: only keep entries with the shape we expect.
    return parsed
      .filter((m) => m && (m.sender === "user" || m.sender === "bot") && typeof m.id === "string")
      .map((m) => ({
        id: m.id,
        sender: m.sender,
        message: typeof m.message === "string" ? m.message : "",
        ...(m.hasError ? { hasError: true } : {}),
        ...(m.errorKind ? { errorKind: m.errorKind } : {}),
        ...(m.attachment && typeof m.attachment.name === "string"
          ? {
              attachment: {
                name: m.attachment.name,
                size: typeof m.attachment.size === "number" ? m.attachment.size : 0,
                type: typeof m.attachment.type === "string" ? m.attachment.type : "",
              },
            }
          : {}),
      }));
  } catch {
    return [];
  }
};

const writeStoredMessages = (messages) => {
  try {
    if (!messages || messages.length === 0) {
      sessionStorage.removeItem(MESSAGES_KEY);
      return;
    }
    sessionStorage.setItem(
      MESSAGES_KEY,
      JSON.stringify(messages.map(sanitizeMessageForStorage))
    );
  } catch {
    // Same fallback as session_id — silently no-op if storage rejects.
  }
};

export const TEXT_TYPES = {
  "application/pdf": [".pdf"],
  "text/plain": [".txt"],
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [".docx"],
};

export const IMAGE_TYPES = {
  "image/png": [".png"],
  "image/jpeg": [".jpg", ".jpeg"],
  "image/webp": [".webp"],
  "image/gif": [".gif"],
};

export const TEXT_MAX_BYTES = 25 * 1024 * 1024; // 25MB raw upload cap (server enforces ~100k tokens after extraction)
export const IMAGE_MAX_BYTES = 10 * 1024 * 1024; // 10MB

const TEXT_EXT = [".pdf", ".txt", ".docx"];
const IMAGE_EXT = [".png", ".jpg", ".jpeg", ".webp", ".gif"];

const SUPPORTED_LIST_MESSAGE =
  "Supported types: PDF, TXT, DOCX (up to 25 MB), or PNG, JPG, WEBP, GIF (up to 10 MB).";

const extOf = (name = "") => {
  const dot = name.lastIndexOf(".");
  return dot === -1 ? "" : name.slice(dot).toLowerCase();
};

export const classifyFile = (file) => {
  if (!file) return { kind: null };
  const ext = extOf(file.name);
  if (TEXT_EXT.includes(ext)) return { kind: "text" };
  if (IMAGE_EXT.includes(ext)) return { kind: "image" };
  return { kind: null };
};

export const validateFile = (file) => {
  const { kind } = classifyFile(file);
  if (!kind) {
    return {
      ok: false,
      message: `Unsupported file type. ${SUPPORTED_LIST_MESSAGE}`,
    };
  }
  if (kind === "image" && file.size > IMAGE_MAX_BYTES) {
    return {
      ok: false,
      message: "Image is too large. Images must be 10 MB or smaller.",
    };
  }
  if (kind === "text" && file.size > TEXT_MAX_BYTES) {
    return {
      ok: false,
      message:
        "File is too large. Text files must be 25 MB or smaller. For larger documents, use a regular chat.",
    };
  }
  return { ok: true };
};

const isExpiredStatus = (status) => status === 404 || status === 410;

const errorFromStatus = (status, payloadMessage) => {
  if (status === 401) return "Your session has expired. Please sign in again.";
  if (status === 503) return "Quick chat is busy, try again shortly.";
  if (status === 500 && !payloadMessage) return "Something went wrong, try again.";
  // 400/413/415/422 — server provides a user-friendly message; surface it verbatim.
  return payloadMessage || "Something went wrong, try again.";
};

const useIncognitoChat = ({ showSnackbar }) => {
  // Distinguish "user reloaded the page" (POP, plus initial loads and
  // browser back/forward) from "user actively navigated here from another
  // route" (PUSH / REPLACE — clicking a Link or calling navigate()).
  // Reload preserves persisted state; an active nav-back wipes it so the user
  // gets a fresh quick chat.
  const navType = useNavigationType();
  const navClearGuard = useRef(false);
  if (!navClearGuard.current) {
    navClearGuard.current = true;
    if (navType === "PUSH" || navType === "REPLACE") {
      writeStoredSession(null);
      writeStoredMessages(null);
    }
  }

  // Lazy initialiser pattern — these run once at mount, not on every render.
  // After the nav-driven clear above, storage is empty for active navigations,
  // so a remount-via-link reads as if no prior session existed.
  const [messages, setMessages] = useState(() => readStoredMessages());
  const [attachedFile, setAttachedFile] = useState(null);
  const [isSending, setIsSending] = useState(false);
  const [expiryNotice, setExpiryNotice] = useState(null);

  // One-time lazy init for the session_id ref — refs don't accept lazy
  // initialisers, so we guard with the sentinel value.
  const sessionIdRef = useRef(undefined);
  if (sessionIdRef.current === undefined) {
    sessionIdRef.current = readStoredSession();
  }

  // Mirror the message list to sessionStorage. Effect runs after every commit,
  // which is fine — sessionStorage writes are synchronous and fast.
  useEffect(() => {
    writeStoredMessages(messages);
  }, [messages]);

  const { isStreaming, streamingMessageId, streamChat, stopStreaming } =
    useStreamingChat({ showSnackbar, updateTokenUsage: undefined });

  const attachFile = useCallback(
    (file) => {
      const result = validateFile(file);
      if (!result.ok) {
        showSnackbar(result.message, "error");
        return false;
      }
      setAttachedFile(file);
      return true;
    },
    [showSnackbar]
  );

  const removeFile = useCallback(() => {
    setAttachedFile(null);
  }, []);

  const dismissExpiryNotice = useCallback(() => {
    setExpiryNotice(null);
  }, []);

  const reset = useCallback(() => {
    sessionIdRef.current = null;
    writeStoredSession(null);
    writeStoredMessages(null);
    setMessages([]);
    setAttachedFile(null);
    setExpiryNotice(null);
    setIsSending(false);
  }, []);

  const sendMessage = useCallback(
    async (queryText) => {
      const trimmed = (queryText || "").trim();
      if (!trimmed) return;
      if (isSending || isStreaming) return;

      // Clear any stale expiry notice when the user re-engages.
      setExpiryNotice(null);

      const settings = loadSettings();
      const streamingEnabled = !!settings.streaming;

      const AuthToken = localStorage.getItem("authToken");
      const UserToken = localStorage.getItem("userToken");

      const formData = new FormData();
      formData.append("query", trimmed);
      if (sessionIdRef.current) {
        formData.append("session_id", sessionIdRef.current);
      }
      try {
        formData.append(
          "user_settings",
          JSON.stringify({
            response_length: settings.response_length,
            temperature: settings.temperature,
          })
        );
      } catch {
        // ignore — server will use defaults
      }
      if (attachedFile) {
        formData.append("file", attachedFile);
      }

      // Snapshot the file's display metadata onto the user message itself —
      // the bubble will render an inline doc tag, and the composer chip clears
      // on success so it stops looking like a long-standing detachable thing.
      // (The actual File object stays only as long as the request needs it.)
      const attachmentSnapshot = attachedFile
        ? { name: attachedFile.name, size: attachedFile.size, type: attachedFile.type }
        : null;

      const userMessage = {
        id: `u-${Date.now()}`,
        sender: "user",
        message: trimmed,
        ...(attachmentSnapshot ? { attachment: attachmentSnapshot } : {}),
      };
      const placeholderId = `b-${Date.now()}`;
      const placeholder = streamingEnabled
        ? {
            id: placeholderId,
            sender: "bot",
            message: "",
            isStreaming: true,
            streamingSupported: true,
          }
        : { id: placeholderId, sender: "bot", message: "", isLoading: true };

      setMessages((prev) => [...prev, userMessage, placeholder]);
      setIsSending(true);

      const headers = {};
      if (AuthToken) headers["authorization-token"] = AuthToken;
      if (UserToken) headers["user-token"] = UserToken;

      // Track the bot message id locally — onStart reassigns it during streaming.
      // Avoids stale `streamingMessageId` from React closure on the first send.
      let activeBotId = placeholderId;

      try {
        const response = await streamChat({
          endpoint: API_ENDPOINTS.IncognitoChat,
          headers,
          body: formData,
          streamingEnabled,
          onStart: (messageId) => {
            activeBotId = messageId;
            setMessages((prev) =>
              prev.map((m) => (m.id === placeholderId ? { ...m, id: messageId } : m))
            );
          },
          onChunk: (chunk) => {
            if (chunk.type === "metadata") {
              if (chunk.data && chunk.data.session_id) {
                sessionIdRef.current = chunk.data.session_id;
                writeStoredSession(chunk.data.session_id);
              }
              return;
            }
            if (chunk.type === "content") {
              setMessages((prev) => {
                const lastIdx = prev.length - 1;
                if (lastIdx < 0 || prev[lastIdx].sender !== "bot") return prev;
                const next = [...prev];
                next[lastIdx] = {
                  ...next[lastIdx],
                  message: (next[lastIdx].message || "") + (chunk.data?.content || ""),
                };
                return next;
              });
              return;
            }
            if (chunk.type === "error") {
              showSnackbar(chunk.data?.message || "Error in stream", "error");
            }
          },
          onComplete: (finalResponse, messageId) => {
            const final = finalResponse || {};
            if (final.session_id) {
              sessionIdRef.current = final.session_id;
              writeStoredSession(final.session_id);
            }
            setMessages((prev) =>
              prev.map((m) => {
                if (m.id !== messageId) return m;
                return {
                  ...m,
                  isStreaming: false,
                  message: final.response || m.message || "",
                };
              })
            );
            // The file (if any) was attached to the user message snapshot above
            // and is now visually anchored there. Clear the composer chip so it
            // doesn't look like a separate detachable thing for subsequent turns.
            if (attachmentSnapshot) setAttachedFile(null);
            setIsSending(false);
          },
          onError: (error) => {
            const status = error?.status;
            const payloadMsg = error?.payload?.message;

            if (isExpiredStatus(status)) {
              // Server-side TTL elapsed. Wipe session (memory + sessionStorage),
              // drop the placeholder and the user message that just couldn't
              // reach a session, post a brief inline notice, restore the unsent
              // draft to the composer.
              sessionIdRef.current = null;
              writeStoredSession(null);
              setMessages((prev) =>
                prev.filter((m) => m.id !== activeBotId && m.id !== userMessage.id)
              );
              setExpiryNotice({
                kind: "expired",
                message: "This quick chat expired. Starting a new one…",
                draft: trimmed,
              });
              setIsSending(false);
              return;
            }

            if (status === 413) {
              setMessages((prev) =>
                prev.map((m) => {
                  if (m.id !== activeBotId) return m;
                  return {
                    ...m,
                    isStreaming: false,
                    isLoading: false,
                    hasError: true,
                    errorKind: "size",
                    message:
                      payloadMsg ||
                      "File or message too large. Use a regular chat for large documents.",
                  };
                })
              );
              setIsSending(false);
              return;
            }

            const inlineMessage = errorFromStatus(status, payloadMsg);
            setMessages((prev) =>
              prev.map((m) => {
                if (m.id !== activeBotId) return m;
                return {
                  ...m,
                  isStreaming: false,
                  isLoading: false,
                  hasError: true,
                  message: inlineMessage,
                };
              })
            );
            setIsSending(false);
          },
        });

        if (!streamingEnabled && response) {
          if (response.session_id) {
            sessionIdRef.current = response.session_id;
            writeStoredSession(response.session_id);
          }
          setMessages((prev) =>
            prev.map((m) => {
              if (m.id !== placeholderId) return m;
              return {
                ...m,
                isLoading: false,
                message: response.response || "",
              };
            })
          );
          if (attachmentSnapshot) setAttachedFile(null);
          setIsSending(false);
        } else if (!streamingEnabled && !response) {
          setIsSending(false);
        }
      } catch (e) {
        showSnackbar("Something went wrong, try again.", "error");
        setIsSending(false);
      }
    },
    [attachedFile, isSending, isStreaming, showSnackbar, streamChat]
  );

  return {
    messages,
    attachedFile,
    attachFile,
    removeFile,
    sendMessage,
    isSending,
    isStreaming,
    streamingMessageId,
    stopStreaming,
    expiryNotice,
    dismissExpiryNotice,
    reset,
  };
};

export default useIncognitoChat;
